"use client";

import { useState, useRef, useEffect } from "react";
import Button from "react-bootstrap/Button";

const SingleSelect = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select option...",
  className = "",
  maxHeight = 300,
  isClearable = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle option selection
  const handleOptionSelect = (option) => {
    onChange && onChange(option);
    setIsOpen(false); // Close dropdown after selection
  };

  // Handle clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    onChange && onChange(null);
  };

  // Get display text for the trigger button
  const getDisplayText = () => {
    if (!value) return placeholder;
    return value.label || value.title || value;
  };

  return (
    <div className={`position-relative ${className}`} ref={containerRef}>
      {/* Trigger Button */}
      <div className="position-relative">
        <Button
          variant="outline-secondary"
          className="w-100 d-flex justify-content-between align-items-center text-start pe-5"
          style={{
            backgroundColor: "#fff",
            borderColor: "#ced4da",
            color: !value ? "#6c757d" : "#212529",
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-truncate">{getDisplayText()}</span>
          <i className={`fa fa-chevron-${isOpen ? "up" : "down"} ms-2`} />
        </Button>

        {/* Clear button when item is selected */}
        {value && isClearable && (
          <Button
            variant="link"
            size="sm"
            className="position-absolute"
            style={{
              right: "30px",
              top: "50%",
              transform: "translateY(-50%)",
              padding: "2px 6px",
              color: "#6c757d",
              zIndex: 1,
            }}
            onClick={handleClear}
            title="Clear selection"
          >
            <i className="fa fa-times" />
          </Button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{
            top: "100%",
            left: 0,
            zIndex: 1050,
            marginTop: "2px",
            maxHeight: maxHeight,
            overflowY: "auto",
          }}
        >
          {/* Options List */}
          <div className="py-1">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-muted">
                <i className="fa fa-info-circle me-2" />
                No options available
              </div>
            ) : (
              options.map((option, index) => {
                const isSelected =
                  value &&
                  (value.value === option.value || value === option.value);
                return (
                  <div
                    key={option.value || index}
                    className={`px-3 py-2 hover-bg-light ${
                      isSelected ? "selected-option" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleOptionSelect(option)}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{option.label || option.title}</span>
                      {isSelected && <i className="fa fa-check text-primary" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Custom CSS for hover effect */}
      <style jsx>{`
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
        .selected-option {
          background-color: #e3f2fd !important;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default SingleSelect;
